import { useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../common/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STUN_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
];

/**
 * PeerHost: 학생(방 주인)측 P2P 호스트.
 * - 시그널링 폴링으로 관찰자의 offer 수신
 * - 각 관찰자별 PeerConnection 생성 + answer 전송
 * - DataChannel로 카메라 스냅샷 + 상태 데이터 전송
 *
 * 설계: PeerHost는 수동적. 관찰자의 offer를 받아 answer를 보내고,
 * 연결이 끊기면 피어를 삭제. 재연결은 관찰자(PeerManager)가 새 offer를 보내면서 시작.
 */
const PeerHost = ({ snapshotBlobRef, studyStatus }) => {
    const peersRef = useRef(new Map()); // key: observerUserId, value: { pc, statusChannel, snapshotChannel }
    const pollingRef = useRef(null);
    const mountedRef = useRef(true);
    const iceCandidateQueues = useRef(new Map()); // key: observerUserId, value: [RTCIceCandidate]
    const lastSnapshotBufferRef = useRef(null);

    const sendSignal = useCallback(async (targetUserId, type, data) => {
        try {
            await authFetch(`${BACKEND_URL}/api/v1/studyrooms/${targetUserId}/signal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type, data })
            });
        } catch (error) {
            console.error('PeerHost: Failed to send signal:', error);
        }
    }, []);

    const createPeerForObserver = useCallback((observerId) => {
        // 이미 연결이 있으면 정리 후 재생성
        if (peersRef.current.has(observerId)) {
            const old = peersRef.current.get(observerId);
            if (old.statusChannel) try { old.statusChannel.close(); } catch {}
            if (old.snapshotChannel) try { old.snapshotChannel.close(); } catch {}
            old.pc.close();
            peersRef.current.delete(observerId);
        }
        // 이전 ICE 큐도 정리 (stale candidate 방지)
        iceCandidateQueues.current.delete(observerId);

        const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });

        pc.onicecandidate = (event) => {
            if (event.candidate && mountedRef.current) {
                sendSignal(observerId, 'ice', JSON.stringify(event.candidate));
            }
        };

        pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState;
            if (state === 'disconnected' || state === 'failed' || state === 'closed') {
                const peer = peersRef.current.get(observerId);
                if (peer) {
                    if (peer.statusChannel) try { peer.statusChannel.close(); } catch {}
                    if (peer.snapshotChannel) try { peer.snapshotChannel.close(); } catch {}
                    peer.pc.close();
                    peersRef.current.delete(observerId);
                    iceCandidateQueues.current.delete(observerId);
                }
            }
        };

        // 관찰자가 만든 DataChannel 수신
        const peer = { pc, statusChannel: null, snapshotChannel: null };

        pc.ondatachannel = (event) => {
            const channel = event.channel;
            if (channel.label === 'status') {
                peer.statusChannel = channel;
            } else if (channel.label === 'snapshot') {
                peer.snapshotChannel = channel;
            }
        };

        peersRef.current.set(observerId, peer);
        return pc;
    }, [sendSignal]);

    const flushIceQueue = useCallback(async (observerId, pc) => {
        const queue = iceCandidateQueues.current.get(observerId) || [];
        iceCandidateQueues.current.delete(observerId);
        for (const candidate of queue) {
            try {
                await pc.addIceCandidate(candidate);
            } catch (e) {
                console.warn('PeerHost: Failed to add queued ICE:', e);
            }
        }
    }, []);

    const handleSignalingMessage = useCallback(async (msg) => {
        if (!mountedRef.current) return;

        try {
            if (msg.type === 'offer') {
                const pc = createPeerForObserver(msg.from);
                await pc.setRemoteDescription(JSON.parse(msg.data));
                await flushIceQueue(msg.from, pc);
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                await sendSignal(msg.from, 'answer', JSON.stringify(answer));
            } else if (msg.type === 'ice') {
                const peer = peersRef.current.get(msg.from);
                if (peer && peer.pc.remoteDescription) {
                    await peer.pc.addIceCandidate(JSON.parse(msg.data));
                } else {
                    // offer 도착 전 또는 remoteDescription 설정 전 ICE → 큐에 버퍼링
                    if (!iceCandidateQueues.current.has(msg.from)) {
                        iceCandidateQueues.current.set(msg.from, []);
                    }
                    iceCandidateQueues.current.get(msg.from).push(JSON.parse(msg.data));
                }
            }
        } catch (error) {
            console.error('PeerHost: Failed to handle signal:', error);
        }
    }, [createPeerForObserver, sendSignal, flushIceQueue]);

    // 시그널링 폴링: 관찰자의 offer/ICE 수신
    useEffect(() => {
        mountedRef.current = true;

        const pollSignals = async () => {
            if (!mountedRef.current) return;
            try {
                const response = await authFetch(`${BACKEND_URL}/api/v1/studyrooms/signal/pending`);
                const data = await response.json();
                if (data.success && data.messages.length > 0) {
                    // offer를 먼저 처리하고 ice를 나중에 처리 (순서 보장)
                    const offers = data.messages.filter(m => m.type === 'offer');
                    const ices = data.messages.filter(m => m.type === 'ice');
                    for (const msg of offers) {
                        await handleSignalingMessage(msg);
                    }
                    for (const msg of ices) {
                        await handleSignalingMessage(msg);
                    }
                }
            } catch (error) {
                // silent
            }
        };

        pollSignals();
        pollingRef.current = setInterval(pollSignals, 2000);

        return () => {
            mountedRef.current = false;
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            // 모든 PeerConnection 정리
            for (const [, peer] of peersRef.current) {
                if (peer.statusChannel) try { peer.statusChannel.close(); } catch {}
                if (peer.snapshotChannel) try { peer.snapshotChannel.close(); } catch {}
                peer.pc.close();
            }
            peersRef.current.clear();
            iceCandidateQueues.current.clear();
        };
    }, [handleSignalingMessage]);

    // DataChannel로 스냅샷 + 상태 전송 (1초 간격)
    useEffect(() => {
        const sendInterval = setInterval(async () => {
            if (!mountedRef.current || peersRef.current.size === 0) return;

            // 스냅샷 버퍼를 한 번만 변환 (모든 피어에 동일 버퍼 전송)
            let snapshotBuffer = null;
            if (snapshotBlobRef.current) {
                try {
                    snapshotBuffer = await snapshotBlobRef.current.arrayBuffer();
                    lastSnapshotBufferRef.current = snapshotBuffer;
                } catch {
                    snapshotBuffer = lastSnapshotBufferRef.current;
                }
            }

            for (const [, peer] of peersRef.current) {
                // 상태 메시지 전송
                if (peer.statusChannel && peer.statusChannel.readyState === 'open') {
                    try {
                        peer.statusChannel.send(JSON.stringify({
                            type: 'status',
                            ...studyStatus
                        }));
                    } catch {
                        // channel might have closed
                    }
                }

                // 스냅샷 전송
                if (peer.snapshotChannel && peer.snapshotChannel.readyState === 'open' && snapshotBuffer) {
                    try {
                        peer.snapshotChannel.send(snapshotBuffer);
                    } catch {
                        // channel might have closed
                    }
                }
            }
        }, 1000);

        return () => clearInterval(sendInterval);
    }, [snapshotBlobRef, studyStatus]);

    return null;
};

export default PeerHost;
